import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAlocacao } from '../utils/generators/grade_alocaoes.generator';
import { AlocacoesQuery_alocacoes_professores } from '../graphql/queries/__generated__/AlocacoesQuery';
import { FeriadosQuery_feriados } from '../graphql/queries/__generated__/FeriadosQuery';

import {
  AlocacaoPageQuery_disciplinas,
  AlocacaoPageQuery_horarios,
  AlocacaoPageQuery_periodos,
  AlocacaoPageQuery_professores,
} from '../graphql/queries/__generated__/AlocacaoPageQuery';

export interface IGradeAlocacao {
  id_grade: string;
  id_oferta: number;
  id_turma: number;
  id_periodo: number;
  dsc_nome_grade: string;
  alocacoes: AlocacoesQuery_alocacoes_professores[];
  feriados: FeriadosQuery_feriados[];
  dat_inicio: string;
  dat_fim: string;
}

export interface ISelecaoAlocacao {
  id_oferta: number;
  id_turma: number;
  alocacao: IAlocacao;
}

export interface IProfessorAlocacoes {
  periodo: AlocacaoPageQuery_periodos | null;
  professor: AlocacaoPageQuery_professores | null;
  ind_status: number | null;
  disciplina: AlocacaoPageQuery_disciplinas | null;
  horario: AlocacaoPageQuery_horarios | null;
  alocacoes: ISelecaoAlocacao[];
  ativos: boolean;
}

export interface ICorProfessor {
  id_cor: number;
  id_professor: number;
}

const initialState = {
  ocultar_dias: new Array<string>(),
  cores_professores: new Array<ICorProfessor>(),
  grades: new Array<IGradeAlocacao>(),
  selecao: {
    periodo: null,
    professor: null,
    ind_status: null,
    disciplina: null,
    horario: null,
    alocacoes: new Array<ISelecaoAlocacao>(),
    ativos: false,
  } as IProfessorAlocacoes,
};

const stock = createSlice({
  name: 'alocacao',
  initialState,
  reducers: {
    selecionarTurma(state, action: PayloadAction<IGradeAlocacao>) {
      state.grades.push(action.payload);
    },
    desselecionarTurma(state, action: PayloadAction<string>) {
      const index = state.grades.findIndex((grade) => grade.id_grade === action.payload);
      state.grades.splice(index, 1);
    },
    limparTurmas(state) {
      state.grades = [];
      state.selecao.alocacoes = [];
    },
    ordenarTurmas(state) {
      state.grades = state.grades.sort((a, b) => {
        if (a.dsc_nome_grade < b.dsc_nome_grade) {
          return -1;
        }
        if (a.dsc_nome_grade > b.dsc_nome_grade) {
          return 1;
        }
        return 0;
      });
    },
    alternarDia(state, action: PayloadAction<string>) {
      if (!state.ocultar_dias.includes(action.payload)) {
        state.ocultar_dias.push(action.payload);
      } else {
        const index = state.ocultar_dias.indexOf(action.payload);
        state.ocultar_dias.splice(index, 1);
      }
    },
    selecionarPeriodo(state, action: PayloadAction<AlocacaoPageQuery_periodos>) {
      state.selecao.periodo = action.payload;
    },
    selecionarProfessor(state, action: PayloadAction<AlocacaoPageQuery_professores>) {
      state.selecao.professor = action.payload;
    },
    selecionarCor(state, action: PayloadAction<{ id_professor: number; id_cor: number }>) {
      const index = state.cores_professores.findIndex(
        (c) => c.id_professor === action.payload.id_professor
      );

      if (index === -1) {
        state.cores_professores.push(action.payload);
      } else {
        state.cores_professores[index].id_cor = action.payload.id_cor;
      }
    },
    selecionarDisciplina(state, action: PayloadAction<AlocacaoPageQuery_disciplinas>) {
      state.selecao.disciplina = action.payload;
    },
    selecionarHorario(state, action: PayloadAction<AlocacaoPageQuery_horarios>) {
      state.selecao.horario = action.payload;
    },
    selecionarAlocacao(state, action: PayloadAction<ISelecaoAlocacao>) {
      const index = state.selecao.alocacoes.findIndex((a) => {
        return (
          a.id_oferta === action.payload.id_oferta &&
          a.id_turma === action.payload.id_turma &&
          a.alocacao.dat_alocacao === action.payload.alocacao.dat_alocacao
        );
      });

      if (index === -1) {
        state.selecao.alocacoes.push(action.payload);
      } else {
        state.selecao.alocacoes.splice(index, 1);
      }
    },
    substituirSelecaoAlocacao(state, action: PayloadAction<ISelecaoAlocacao>) {
      const index = state.selecao.alocacoes.findIndex((a) => {
        return (
          a.id_oferta === action.payload.id_oferta &&
          a.id_turma === action.payload.id_turma &&
          a.alocacao.dat_alocacao === action.payload.alocacao.dat_alocacao
        );
      });

      if (index === -1) {
        state.selecao.alocacoes.push(action.payload);
      } else {
        state.selecao.alocacoes[index] = action.payload;
      }
    },
    removerSelecaoAlocacao(state, action: PayloadAction<{ id_alocacao: number }>) {
      const index = state.selecao.alocacoes.findIndex((a) => {
        return a.alocacao.id_alocacao === action.payload.id_alocacao;
      });

      if (index !== -1) {
        state.selecao.alocacoes.splice(index, 1);
      }
    },
    alternarAtivos(state) {
      state.selecao.ativos = state.selecao.ativos ? false : true;
    },
    limparSelecao(state, action: PayloadAction<'alocar' | 'desalocar'>) {
      // manter dia de alocação se for desalocar
      if (action.payload === 'alocar') {
        state.selecao.alocacoes = [];
      } else {
        state.selecao.alocacoes.forEach((a) => {
          a.alocacao = {
            ...a.alocacao,
            professor: undefined,
            disciplina: undefined,
            id_cor: undefined,
          };
        });
      }
    },
    inserirAlocacao(state, action: PayloadAction<AlocacoesQuery_alocacoes_professores>) {
      const id_grade = `${action.payload.id_oferta}.${action.payload.id_turma}`;
      const grade = state.grades.find((g) => g.id_grade === id_grade);

      if (grade) {
        grade.alocacoes.push(action.payload);
      }
    },
    confirmarAlocacao(
      state,
      action: PayloadAction<{
        id_oferta: number;
        id_turma: number;
        id_alocacao: number;
      }>
    ) {
      const id_grade = `${action.payload.id_oferta}.${action.payload.id_turma}`;
      const grade = state.grades.find((g) => g.id_grade === id_grade);

      if (grade) {
        const index = grade.alocacoes.findIndex(
          (a) => a.id_alocacao === action.payload.id_alocacao
        );

        if (index !== -1) {
          grade.alocacoes[index].planejamento = false;
        }
      }
    },
    removerAlocacao(
      state,
      action: PayloadAction<{
        id_oferta: number;
        id_turma: number;
        id_alocacao: number;
      }>
    ) {
      const id_grade = `${action.payload.id_oferta}.${action.payload.id_turma}`;

      const grade = state.grades.find((g) => g.id_grade === id_grade);

      if (grade) {
        const index = grade.alocacoes.findIndex(
          (a) => a.id_alocacao === action.payload.id_alocacao
        );

        if (index !== -1) {
          grade.alocacoes.splice(index, 1);
        }
      }
    },
    limparDados() {
      return initialState;
    },
  },
});

export const {
  selecionarTurma,
  desselecionarTurma,
  limparTurmas,
  ordenarTurmas,
  alternarDia,
  selecionarPeriodo,
  selecionarProfessor,
  selecionarCor,
  selecionarDisciplina,
  selecionarHorario,
  selecionarAlocacao,
  substituirSelecaoAlocacao,
  removerSelecaoAlocacao,
  alternarAtivos,
  limparSelecao,
  inserirAlocacao,
  removerAlocacao,
  limparDados,
  confirmarAlocacao,
} = stock.actions;

export default stock.reducer;
